import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportRenderError } from "../lib/errors";

// App-wide safety net. Without this, a single render-time throw anywhere in
// the tree unmounts the whole PWA to a blank white screen (React's default
// on an uncaught error) — the "trying to X goes to a blank screen" class of
// bug. Here we instead keep the app shell, show a recoverable card, and
// report the real exception (message + component stack) to /api/errors so the
// crash is diagnosable from logs instead of invisible.

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportRenderError(error, info.componentStack ?? undefined);
  }

  private reset = () => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        className="fixed inset-0 z-50 flex items-center justify-center bg-paper p-6"
      >
        <div className="w-full max-w-sm rounded-card bg-paper p-6 text-center shadow-card ring-1 ring-ink/10">
          <p className="font-display text-lg font-semibold text-ink">
            Something went wrong
          </p>
          <p className="mt-2 text-sm text-ink/60">
            That screen hit an unexpected error. Your saved data is safe.
            Try again, or reload the app.
          </p>
          <div className="mt-5 flex flex-col gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="inline-flex min-h-[44px] items-center justify-center rounded-full bg-grad-cta px-5 py-2 text-sm font-bold text-white shadow-card transition hover:opacity-95"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex min-h-[44px] items-center justify-center rounded-full px-5 py-2 text-sm font-bold text-ink/70 ring-1 ring-inset ring-ink/10 hover:bg-ink/5"
            >
              Reload
            </button>
          </div>
          {/* The message helps the user relay what broke; the stack stays in
              the logged report, not on screen. */}
          <p className="mt-4 break-words text-xs text-ink/35">{error.message}</p>
        </div>
      </div>
    );
  }
}
